import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_APP_API_BASE_URL || "";

const InviteAcceptance = () => {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);
  const [result, setResult] = useState(null);

  // Reject/Reschedule forms
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showRescheduleForm, setShowRescheduleForm] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleRemark, setRescheduleRemark] = useState("");

  useEffect(() => { document.title = "Interview Invitation - Ample Hire"; }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/ai/invite/public/${token}`)
      .then((r) => setInvite(r.data))
      .catch(() => setError("This invite link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    setResponding(true);
    try {
      const r = await axios.post(`${API_BASE}/ai/invite/public/${token}/accept`);
      if (r.data.ok === false) {
        setResult({ type: "warning", message: r.data.message });
      } else {
        setResult({ type: "success", message: r.data.message });
        setInvite((p) => ({ ...p, status: "ACCEPTED" }));
      }
    } catch (e) {
      setResult({ type: "error", message: e.response?.data?.message || "Something went wrong." });
    } finally { setResponding(false); }
  };

  const handleReject = async () => {
    setResponding(true);
    try {
      const r = await axios.post(`${API_BASE}/ai/invite/public/${token}/reject`, { remark: rejectRemark || null });
      if (r.data.ok === false) {
        setResult({ type: "warning", message: r.data.message });
      } else {
        setResult({ type: "info", message: r.data.message });
        setInvite((p) => ({ ...p, status: "REJECTED" }));
      }
    } catch (e) {
      setResult({ type: "error", message: e.response?.data?.message || "Something went wrong." });
    } finally { setResponding(false); setShowRejectForm(false); }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate) return alert("Please select a preferred date.");
    setResponding(true);
    try {
      const r = await axios.post(`${API_BASE}/ai/invite/public/${token}/reschedule`, {
        date: rescheduleDate, time: rescheduleTime || null, remark: rescheduleRemark || null,
      });
      if (r.data.ok === false) {
        setResult({ type: "warning", message: r.data.message });
      } else {
        setResult({ type: "info", message: r.data.message });
        setInvite((p) => ({ ...p, status: "RESCHEDULED" }));
      }
    } catch (e) {
      setResult({ type: "error", message: e.response?.data?.message || "Something went wrong." });
    } finally { setResponding(false); setShowRescheduleForm(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-vf-bg flex items-center justify-center p-6">
      <div className="w-8 h-8 border-2 border-vf-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-vf-bg flex items-center justify-center p-6">
      <div className="card p-10 max-w-md w-full text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4 text-xl font-bold">!</div>
        <h2 className="font-semibold text-lg text-vf-base mb-2">Invite Not Found</h2>
        <p className="text-sm text-vf-muted">{error}</p>
      </div>
    </div>
  );

  const canRespond = invite.status === "PENDING" || invite.status === "RESCHEDULED";

  return (
    <div className="min-h-screen bg-vf-bg flex items-center justify-center p-4">
      <div className="card max-w-xl w-full overflow-hidden p-0">
        {/* Header */}
        <div className="bg-vf-base px-8 py-6">
          <img src="https://amplepayroll.com/wp-content/uploads/2024/06/ample_black-1.png" alt="Ample" className="h-8 brightness-0 invert" />
        </div>

        <div className="p-8 space-y-6">
          <div>
            <h1 className="text-xl font-semibold text-vf-base">
              Hi {invite.candidateName || "there"},
            </h1>
            <p className="text-sm text-vf-muted mt-2 leading-relaxed">
              You've been invited for an AI-powered interview. Review the details below.
            </p>
          </div>

          {/* Job Details */}
          <div className="bg-vf-tint rounded-xl p-5 space-y-4">
            <div>
              <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-1">Position</div>
              <div className="text-lg font-semibold text-vf-base">{invite.jobTitle || "—"}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {invite.location && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-0.5">Location</div>
                  <div className="text-sm font-medium text-vf-base">{invite.location}</div>
                </div>
              )}
              {invite.employmentType && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-0.5">Type</div>
                  <div className="text-sm font-medium text-vf-base">{invite.employmentType.replace(/_/g, " ")}</div>
                </div>
              )}
              {(invite.minYearsExperience != null || invite.maxYearsExperience != null) && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-0.5">Experience</div>
                  <div className="text-sm font-medium text-vf-base">{invite.minYearsExperience ?? 0}–{invite.maxYearsExperience ?? "?"} years</div>
                </div>
              )}
              {(invite.interviewDate || invite.interviewTime) && (
                <div>
                  <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-0.5">Schedule</div>
                  <div className="text-sm font-medium text-vf-base">
                    {invite.interviewDate || "TBD"}{invite.interviewTime ? ` at ${invite.interviewTime}` : ""}
                  </div>
                </div>
              )}
            </div>
            {invite.requiredSkills && invite.requiredSkills.length > 0 && (
              <div>
                <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-1.5">Skills</div>
                <div className="flex flex-wrap gap-1.5">
                  {(Array.isArray(invite.requiredSkills) ? invite.requiredSkills : []).map((s, i) => (
                    <span key={i} className="badge">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Job Description */}
          {invite.jobDescription && (
            <div>
              <div className="text-2xs font-semibold uppercase tracking-widest text-vf-muted mb-2">Job Description</div>
              <div className="text-sm text-vf-base leading-relaxed bg-vf-tint rounded-xl p-5 max-h-48 overflow-y-auto whitespace-pre-wrap">
                {invite.jobDescription}
              </div>
            </div>
          )}

          {/* JD + Attachments */}
          {(invite.jdFileUrl || (invite.attachments && invite.attachments.length > 0)) && (
            <div className="space-y-1.5">
              {invite.jdFileUrl && (
                <a href={`${API_BASE}${invite.jdFileUrl}`} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm inline-flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Download JD
                </a>
              )}
              {invite.attachments && invite.attachments.map((att, i) => (
                <a key={i} href={`${API_BASE}${att.downloadUrl}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2 rounded-lg bg-vf-tint text-sm text-vf-base hover:bg-vf-primary/5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-vf-primary"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  {att.fileName}
                </a>
              ))}
            </div>
          )}

          {/* Reschedule info (if previously rescheduled) */}
          {invite.status === "RESCHEDULED" && (
            <div className="bg-yellow-50 rounded-xl p-4 text-sm">
              <div className="font-semibold text-yellow-700 mb-1">Reschedule Requested</div>
              <div className="text-yellow-600">
                Preferred: {invite.rescheduledDate}{invite.rescheduledTime ? ` at ${invite.rescheduledTime}` : ""}
                {invite.remark && <div className="mt-1 italic">"{invite.remark}"</div>}
              </div>
              <div className="text-xs text-yellow-500 mt-2">You can still accept or modify your request below.</div>
            </div>
          )}

          {/* Result message */}
          {result && (
            <div className={`rounded-xl p-4 text-sm font-medium text-center ${
              result.type === "success" ? "bg-green-50 text-green-700" :
              result.type === "info" ? "bg-blue-50 text-blue-700" :
              result.type === "warning" ? "bg-yellow-50 text-yellow-700" :
              "bg-red-50 text-red-600"
            }`}>{result.message}</div>
          )}

          {/* Already responded (on page load) */}
          {!canRespond && !result && (
            <div className={`rounded-xl p-4 text-sm font-medium text-center ${
              invite.status === "ACCEPTED" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
            }`}>
              {invite.status === "ACCEPTED"
                ? "You have already accepted this invitation. Check your email for the interview link."
                : invite.status === "REJECTED"
                ? <>You have declined this invitation.{invite.remark && <div className="mt-1 font-normal italic">"{invite.remark}"</div>}</>
                : `This invitation has been ${invite.status.toLowerCase()}.`}
            </div>
          )}

          {/* Action Buttons */}
          {canRespond && !result && (
            <div className="space-y-3">
              {/* Main buttons */}
              {!showRejectForm && !showRescheduleForm && (
                <div className="flex gap-3">
                  <button onClick={handleAccept} disabled={responding} className="btn-primary flex-1 py-3 text-base disabled:opacity-50">
                    {responding ? "Processing..." : "Accept Interview"}
                  </button>
                  <button onClick={() => setShowRescheduleForm(true)} disabled={responding} className="btn-secondary flex-1 py-3 text-base disabled:opacity-50">
                    Reschedule
                  </button>
                  <button onClick={() => setShowRejectForm(true)} disabled={responding} className="btn-ghost flex-1 py-3 text-base disabled:opacity-50 text-vf-danger">
                    Decline
                  </button>
                </div>
              )}

              {/* Reject Form */}
              {showRejectForm && (
                <div className="bg-red-50 rounded-xl p-5 space-y-3">
                  <div className="text-sm font-semibold text-red-700">Decline Interview</div>
                  <div>
                    <label className="text-xs text-red-600 mb-1 block">Reason (optional)</label>
                    <textarea
                      className="w-full px-3 py-2 rounded-lg text-sm border border-red-200 focus:outline-none focus:ring-2 focus:ring-red-300"
                      rows={3}
                      value={rejectRemark}
                      onChange={(e) => setRejectRemark(e.target.value)}
                      placeholder="e.g. I've accepted another offer, timing doesn't work..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowRejectForm(false)} className="btn-ghost btn-sm flex-1">Cancel</button>
                    <button onClick={handleReject} disabled={responding} className="btn-danger btn-sm flex-1">
                      {responding ? "Submitting..." : "Confirm Decline"}
                    </button>
                  </div>
                </div>
              )}

              {/* Reschedule Form */}
              {showRescheduleForm && (
                <div className="bg-blue-50 rounded-xl p-5 space-y-3">
                  <div className="text-sm font-semibold text-blue-700">Request Reschedule</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-blue-600 mb-1 block">Preferred Date *</label>
                      <input type="date" className="w-full px-3 py-2 rounded-lg text-sm border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]} />
                    </div>
                    <div>
                      <label className="text-xs text-blue-600 mb-1 block">Preferred Time</label>
                      <input type="time" className="w-full px-3 py-2 rounded-lg text-sm border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-blue-600 mb-1 block">Reason (optional)</label>
                    <textarea
                      className="w-full px-3 py-2 rounded-lg text-sm border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                      rows={2}
                      value={rescheduleRemark}
                      onChange={(e) => setRescheduleRemark(e.target.value)}
                      placeholder="e.g. I have a conflict on the original date, would prefer next week..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowRescheduleForm(false)} className="btn-ghost btn-sm flex-1">Cancel</button>
                    <button onClick={handleReschedule} disabled={responding || !rescheduleDate} className="btn-primary btn-sm flex-1">
                      {responding ? "Submitting..." : "Submit Reschedule Request"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="bg-vf-tint rounded-xl p-5">
            <div className="text-sm font-semibold text-vf-primary mb-2">What happens next?</div>
            <ul className="text-xs text-vf-muted space-y-1.5 list-disc ml-4">
              <li>If you accept, you'll receive an email with your unique interview link</li>
              <li>If you reschedule, the hiring team will review and confirm a new time</li>
              <li>The AI interview takes about 15-25 minutes</li>
              <li>You'll need a quiet space, camera, microphone, and stable internet</li>
            </ul>
          </div>
        </div>

        <div className="px-8 py-4 text-center text-2xs text-vf-muted" style={{ borderTop: "0.5px solid rgba(99,102,241,0.15)" }}>
          This invitation was sent by Ample Hire on behalf of the hiring team.
        </div>
      </div>
    </div>
  );
};

export default InviteAcceptance;
